import { NavItemAccessor } from '../../NavList'
import { ComboBoxDropdownItem } from '../types/comboBoxDropdownItem'
import { ComboBoxDropdownSourceItem } from '../types/comboBoxDropdownSourceItem'

interface GetItemsOptions<T> {
  isOpenWithSingleValueSelected: boolean
  isValueInSelectedItems: (value: string) => boolean
  items: ComboBoxDropdownSourceItem<T>[]
  searchQuery?: string
}

const includesSearchString = (testString: string, searchString: string) =>
  testString.toLowerCase().includes(searchString.toLowerCase())

export const getItems = <T>({
  isOpenWithSingleValueSelected = false,
  isValueInSelectedItems,
  items: sourceItems,
  searchQuery = '',
}: GetItemsOptions<T>): ComboBoxDropdownItem[] => {
  const items = sourceItems.map(({ accessor, accessorGroupId, children, id, value }) => ({
    children: String(children || value || ''),
    id,
    value: String(value),
    ...(accessor ? { accessor } : {}),
    ...(accessorGroupId ? { accessorGroupId } : {}),
    ...(value ? { searchKeywords: [String(value)] } : {}),
  }))

  const itemsFiltered = isOpenWithSingleValueSelected
    ? items
    : items.filter(({ accessor, children, searchKeywords, value }) => {
        // we're keeping headers for now
        if (accessor === NavItemAccessor.Header) {
          return true
        }

        // remove values that are already selected
        if (isValueInSelectedItems(value)) {
          return false
        }

        return (
          // search in displayed text:
          includesSearchString(children, searchQuery) ||
          // search in values (added to searchKeywords):
          searchKeywords?.some((keyword) => includesSearchString(keyword, searchQuery))
        )
      })

  // cleanup all accessors/headers which do not have any items assigned
  const itemsWithAccessorsFiltered = itemsFiltered.filter(({ accessor, id }) =>
    accessor === NavItemAccessor.Header ? itemsFiltered.some((item) => item?.accessorGroupId === id) : true,
  )

  // reformat values to include displayData
  return itemsWithAccessorsFiltered.map((item) => ({
    ...item,
    value: {
      value: item.value,
      displayData: {
        title: item.children,
        description: item.value,
      },
    },
  }))
}
